import { useCallback, useEffect, useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import type { RootState } from '../../app/store';
import { TASKROUTER_WSLINK } from '../../constants';
import { MenuStages } from '../../constants/enums';
import { selectAssistCard } from '../../redux/features/assistRequest/assistRequest.selectors';
import {
  assistCardDeadlinePassed,
  assistPoolCleared,
  isAssistCardActive,
} from '../../redux/features/assistRequest/assistRequest.slice';
import {
  selectConfig,
  selectTaskRouterNotificationSound,
  selectTRAgentAutoAssignment,
} from '../../redux/features/config/config.selector';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectUserProfile } from '../../selectors/user';
import { ASSIST_CARD_AUDIO } from '../../sounds/assist_card_audio';
import {
  ASSIST_AUDIO_CLIP_EVENT,
  assistClipStore,
  parseAssistClip,
} from '../../utils/assistRequest/hearAndConfirm/clip';
import { routeAssistPoolEvent } from '../../utils/assistRequest/poolEvents';
import logger from '../../utils/logger';
import { getWebsocketUrl } from '../../utils/network';

/**
 * HITL-1792 (HITL 8.0.0): the lobby's assist pool feed.
 *
 * Joins the console's existing `TASK_CHANNEL_<user id>` socket — the same URL
 * `useTaskRouterWS` opens in auto-assignment mode, with `share: true`, so no
 * second connection is made and `useTaskRouterWS` is untouched. Its
 * `processTaskRouterMessage` ignores events without a `task_id`, so the two
 * subscribers never act on each other's events.
 *
 * Receive only: task-router assigns a request straight to this agent, the
 * card shows at once, and the answer is posted over REST (`useAssistAnswer`).
 * The card's local deadline is kept here too. The guest's audio arrives on the
 * same socket, pushed by live-transcriber (`assist_audio_clip`, HITL-1786):
 * it is held outside Redux for the live card only.
 * Nothing is announced: task-router infers lobby presence from the existing
 * `agent_status_available` heartbeat, the assigned task and heartbeat expiry
 * (contract D5). The pool runs only in auto-assignment mode, where the channel
 * is per agent.
 *
 * Not gated on `assist_requests`: that flag is evaluated per store, and the
 * lobby has no store (its Unleash context keeps the last session's restaurant
 * code, or none after login). task-router checks the flag with the request's
 * store at ingest, so a request only ever arrives for a flag-on store; with
 * the flag off everywhere this hook receives nothing and renders nothing.
 *
 * Reconnection is left to `useTaskRouterWS`. react-use-websocket runs each
 * shared subscriber's own `shouldReconnect` on a close, and any subscriber that
 * reconnects reopens the socket for all of them — so a pool subscriber with
 * its own rules would override the order socket's (no reconnect after a 1005
 * close, its retry budget, `onReconnectStop`). This subscriber stays
 * registered on the URL across a drop, so it receives again as soon as
 * `useTaskRouterWS` has reconnected.
 *
 * Messages are handled in `onMessage` only; `filter: () => false` keeps
 * `lastMessage` unset, so a task-channel message (an audio clip is 0.1–1 MB)
 * never re-renders the lobby through this hook.
 */
const useAssistPool = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const config = useAppSelector(selectConfig);
  const userId = useAppSelector(selectUserProfile)?.id;
  const isTaskAssignmentMode = useAppSelector(selectTRAgentAutoAssignment);
  const canPlaySound = useAppSelector(selectTaskRouterNotificationSound);
  const currentTaskId = useAppSelector(selectCurrentTask);
  const card = useAppSelector(selectAssistCard);
  const isActive = Boolean(isTaskAssignmentMode && userId);

  const hasCurrentTaskRef = useRef(Boolean(currentTaskId));
  hasCurrentTaskRef.current = Boolean(currentTaskId);

  // A clip is for the live card, or for a card that has not shown yet (the
  // two arrive on different hops, in either order). One that lands after its
  // card ended (answered, deadline, order) has no one to play it.
  const receiveClip = (message: any) => {
    const route = parseAssistClip(message);
    const requestId = message?.data?.request_id;
    if ('ignored' in route) {
      logger.debug({
        message: 'Assist clip ignored',
        reason: route.ignored,
        requestId,
        isTR: true,
      });
      return;
    }
    const { card: liveCard, endedIds } = store.getState().assistRequest;
    if (
      isAssistCardActive(liveCard) &&
      liveCard.requestId === route.clip.requestId
    ) {
      assistClipStore.put(route.clip);
      logger.info({
        message: 'Assist clip received',
        requestId,
        durationMs: route.clip.durationMs,
        msAfterCard: Date.now() - liveCard.receivedAt,
        isTR: true,
      });
      return;
    }
    const hasEnded =
      endedIds.includes(route.clip.requestId) ||
      liveCard?.requestId === route.clip.requestId;
    if (hasEnded) {
      logger.debug({
        message: 'Assist clip ignored',
        reason: 'not_live_card',
        requestId,
        isTR: true,
      });
      return;
    }
    assistClipStore.holdEarly(route.clip, Date.now());
    logger.debug({
      message: 'Assist clip held for its card',
      requestId,
      isTR: true,
    });
  };

  // A card that just showed takes the clip that arrived before it.
  const adoptEarlyClip = () => {
    const liveCard = store.getState().assistRequest.card;
    if (!isAssistCardActive(liveCard)) return;
    const adopted = assistClipStore.adoptEarly(liveCard.requestId, Date.now());
    if (!adopted) return;
    logger.info({
      message: 'Assist clip received',
      requestId: liveCard.requestId,
      durationMs: adopted.clip.durationMs,
      msAfterCard: -adopted.waitedMs,
      isTR: true,
    });
  };

  useWebSocket(
    isActive
      ? getWebsocketUrl(
          config,
          `${TASKROUTER_WSLINK}_${userId}`,
          MenuStages.LIVE
        )
      : null,
    {
      share: true,
      // Reconnection belongs to `useTaskRouterWS` (see above).
      shouldReconnect: () => false,
      // Handled in onMessage; never stored, so never a re-render.
      filter: () => false,
      onMessage: (message: MessageEvent) => {
        let parsed;
        try {
          parsed = JSON.parse(message.data);
        } catch {
          return;
        }
        if (parsed?.event === ASSIST_AUDIO_CLIP_EVENT) {
          receiveClip(parsed);
          return;
        }
        const route = routeAssistPoolEvent(parsed, Date.now(), {
          hasCurrentTask: hasCurrentTaskRef.current,
        });
        if ('action' in route) {
          dispatch(route.action);
          adoptEarlyClip();
          return;
        }
        if (route.ignored === 'not_pool_event') return;
        const log =
          route.ignored === 'unsupported_schema' ? logger.warn : logger.debug;
        log({
          message: 'Assist pool event ignored',
          reason: route.ignored,
          event: parsed?.event,
          requestId: parsed?.data?.request_id,
          schemaVersion: parsed?.data?.schema_version,
          isTR: true,
        });
      },
    }
  );

  // The agent has a second or two to act: a short cue of its own says a card
  // is up, once per card. It is not the order-assignment sound and plays on its
  // own Audio element, so a card never cuts an order's sound short (or the
  // reverse). Gated by the same notification-sound setting as orders.
  const liveRequestId = isAssistCardActive(card) ? card.requestId : undefined;
  const liveDeadlineAt = isAssistCardActive(card) ? card.deadlineAt : undefined;
  useEffect(() => {
    if (!liveRequestId || !canPlaySound) return;
    // A browser that blocks the sound must not break the card.
    ASSIST_CARD_AUDIO?.play()?.catch(() => undefined);
  }, [liveRequestId, canPlaySound]);

  // At the card's local deadline an unanswered card ends and nothing is sent:
  // the request went to several agents, and who answered is in the data.
  useEffect(() => {
    if (!liveRequestId || liveDeadlineAt === undefined) return undefined;
    const timer = setTimeout(
      () =>
        dispatch(
          assistCardDeadlinePassed({ requestId: liveRequestId, at: Date.now() })
        ),
      Math.max(0, liveDeadlineAt - Date.now())
    );
    return () => clearTimeout(timer);
  }, [dispatch, liveDeadlineAt, liveRequestId]);

  /**
   * Clears the pool only when it holds something: with no pool events the
   * slice must see no actions (FND-19), and a clear always makes new state.
   */
  const clearPool = useCallback(
    (reason: string) => {
      if (!store.getState().assistRequest.card) return;
      dispatch(assistPoolCleared({ reason, at: Date.now() }));
    },
    [dispatch, store]
  );

  // An order offer always beats an assist card: drop it the moment a task is
  // current. task-router takes the assignment back itself (`order_offer`).
  useEffect(() => {
    if (isActive && currentTaskId) clearPool('order_offer');
  }, [clearPool, isActive, currentTaskId]);

  // Leaving the lobby, or auto-assignment turning off. Nothing is sent: an
  // unanswered card is simply one answer fewer, and its deadline ends it.
  useEffect(() => {
    if (!isActive) return undefined;
    return () => clearPool('pool_inactive');
  }, [clearPool, isActive]);

  return { isActive };
};

export default useAssistPool;
